import { Entity } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { useElementSize } from '@helpers';
import { webOnlyFeat } from '@terminal/config/app.config';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import { useScenarios } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { ChartEmptyStates } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/earnings-chart/ChartEmptyStates';
import { ChartEmpty } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/earnings-chart/chart-empty/ChartEmpty';
import { Expectation } from '@terminal/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './EpsExpectationsSection.module.scss';
import { earningsDataForEmptyState } from './utils';
import { WhatIfChart } from './WhatIfChart';

interface EpsExpectationsSectionProps {
  earningsId: string;
  entity: Entity;
}

const CHART_HEIGHT = 300;
const START_HORIZON = '-2d';
const END_HORIZON = '5d';

export const EpsExpectationsSection = ({
  earningsId,
  entity,
}: EpsExpectationsSectionProps) => {
  const { t } = useTranslation(['earnings', 'whatsNew']);
  const chartWrapperRef = useRef(null);
  const { width } = useElementSize(chartWrapperRef);
  const [expectation, setExpectation] = useState<Expectation>(Expectation.Beat);
  const { navigateToPage } = useNavigateToAssetOverview();
  const [loading, setLoading] = useState(false);

  const {
    data: earningsData,
    isError: isEarningsDataError,
    error: earningsDataError,
    isLoading: earningsLoading,
    isFetching: earningsFetching,
  } = useGetEarningById(earningsId);

  const {
    sendMessage,
    data: scenariosData,
    isLoading: isScenariosLoading,
    error: scenariosError,
  } = useScenarios();

  const hasError = isEarningsDataError || scenariosError;
  const errorMessage = earningsDataError?.message;

  const buttonOptions = useMemo(
    () => [
      {
        label: t('earnings:earningsInsight.drawer.earningsPreview.beats'),
        value: Expectation.Beat,
      },
      {
        label: t('earnings:earningsInsight.drawer.earningsPreview.misses'),
        value: Expectation.Miss,
      },
    ],
    [t]
  );

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();

    if (earningsData?.eps?.surprise !== undefined && expectation) {
      params.append(
        queryKeys.epsFilter,
        expectation === Expectation.Beat ? 'beats' : 'misses'
      );
      params.append(queryKeys.epsValue, earningsData.eps.surprise.toString());
    } else {
      params.append(
        queryKeys.epsFilter,
        expectation === Expectation.Beat ? 'beats' : 'misses'
      );
    }

    return params;
  }, [earningsData, expectation]);

  const mappedScenariosData = useMemo(() => {
    if (scenariosData.length === 0) {
      return [];
    }

    const firstScenario = scenariosData[0];

    return (
      firstScenario?.predictions?.map(item => ({
        high: item.high,
        median: item.median,
        low: item.low,
        label: t(item.horizon),
        horizon: item.horizon,
      })) ?? []
    );
  }, [scenariosData]);

  useEffect(() => {
    setLoading(true);
    const condition = expectation === Expectation.Beat ? 'above' : 'below';
    const value = expectation === Expectation.Beat ? 0.005 : -0.005;

    const payload = {
      entities: [
        {
          tag: entity.tag,
        },
      ],
      conditions: [
        {
          condition: 'above',
          snake: `${entity.tag}.event_earnings_release`,
          value: 0.5,
        },
        {
          condition: condition,
          snake: `${entity.tag}.surprise_absolute_eps`,
          value: value,
        },
      ],
      start_horizon: START_HORIZON,
      end_horizon: END_HORIZON,
      skip_report: true,
      non_overlapping: true,
    };

    sendMessage(payload);
    const timeoutId = window.setTimeout(() => {
      setLoading(false);
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [entity.tag, expectation]);

  const handleExpectationChange = useCallback(
    (index: number) => {
      setExpectation(buttonOptions[index].value);
    },
    [buttonOptions]
  );

  const handleDigIntoData = () => {
    navigateToPage({
      entityTag: entity.tag,
      source: POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER,
      tab: TabsType.Earnings,
      searchParams,
    });
  };

  const entityName = entity.name_short || entity.name;
  const activeButtonLabel =
    buttonOptions
      .find(option => option.value === expectation)
      ?.label?.toLowerCase() ?? '';

  const shouldShowLoader =
    isScenariosLoading || loading || earningsLoading || earningsFetching;

  const renderContent = () => {
    if (shouldShowLoader) {
      return (
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '300px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      );
    }

    if (!earningsData) {
      return (
        <ChartEmpty
          title={t(
            'earnings:earningsInsight.drawer.earningsPreview.emptyTitle'
          )}
          description={t('earnings:empty.description')}
        />
      );
    }

    if (mappedScenariosData.length) {
      return (
        <WhatIfChart
          entityName={entityName}
          scenariosData={scenariosData}
          activeButtonLabel={activeButtonLabel}
          handleDigIntoData={webOnlyFeat(handleDigIntoData)}
          mappedScenariosData={mappedScenariosData}
          width={width}
          chartHeight={CHART_HEIGHT}
        />
      );
    }

    return (
      <ChartEmptyStates
        earningsData={earningsDataForEmptyState(
          earningsData,
          entityName,
          entity.ticker
        )}
        expectation={expectation}
        scenario={null}
      />
    );
  };

  return (
    <div className={styles.root}>
      <div className={styles.buttonGroupWrapper}>
        <span className={styles.titleLabel}>
          {t('earnings:earningsInsight.drawer.earningsPreview.whatIfEps')}
        </span>
        <div className={styles.styledButtonGroup}>
          <ButtonGroup
            activeIndex={buttonOptions.findIndex(
              option => option.value === expectation
            )}
            labels={buttonOptions.map(option => option.label)}
            onClick={handleExpectationChange}
          />
        </div>
        <span className={styles.titleLabel}>
          {t('earnings:earningsInsight.drawer.earningsPreview.expectations')}
        </span>
      </div>
      {hasError && errorMessage && (
        <div data-testid="error-message">{errorMessage}</div>
      )}
      <div ref={chartWrapperRef}>{renderContent()}</div>
    </div>
  );
};
